import React from "react";
import { Ear, Anchor, HandHeart, UserCheck, Building2, LifeBuoy, Check, X, FileText, BookOpen } from "lucide-react";

const doctrines = [
  { icon: Ear, title: "Listen before leading", body: "Make a genuine effort to understand the nurse's experience before redirecting, interpreting, correcting, or solving it." },
  { icon: Anchor, title: "Stabilize before analyzing", body: "When emotional intensity is high, support stability before inviting deep analysis or major decisions." },
  { icon: HandHeart, title: "Preserve agency", body: "The nurse controls the direction, pace, and depth. The system never lectures, pressures, shames, or creates dependence." },
  { icon: UserCheck, title: "Separate the person from the pressure", body: "Help nurses observe thoughts and reactions without letting one difficult event define their identity." },
  { icon: Building2, title: "Recognize that some problems are systemic", body: "Never reinterpret organizational harm as an individual resilience problem." },
  { icon: LifeBuoy, title: "Safety overrides engagement", body: "If a conversation suggests credible, immediate danger, connection with qualified human help comes first — always." },
];

const wouldSay = [
  "That sounds like an exhausting situation.",
  "You seem to be carrying both anger and responsibility.",
  "Would it help to stay with what happened, settle for a moment, or consider what you may need next?",
];
const wouldNever = [
  "You are overreacting.",
  "You need to be more resilient.",
  "This is normal for nurses.",
];

const Doctrine = () => (
  <div data-testid="doctrine">
    {/* Framing */}
    <div className="reveal glass-strong rounded-3xl p-8 sm:p-11 border border-white/10 mb-6">
      <p className="font-body text-zinc-200 leading-relaxed max-w-3xl">
        My Mood Groove would operate through a <span className="text-white">Behavioral Doctrine designed for ANA review, clinical validation, and continuing governance</span>.
        The framework defines how the platform listens, recognizes emotional intensity, preserves agency, distinguishes personal strain
        from organizational problems, and responds when human or emergency support may be required.
      </p>
      <blockquote className="mt-6 border-l-2 border-teal-300/60 pl-5">
        <p className="font-heading text-white text-xl sm:text-2xl leading-snug">
          The AI is not the doctrine. The doctrine governs the AI.<br className="hidden sm:block" />
          The AI is not the destination. The nurse is.
        </p>
      </blockquote>
    </div>

    {/* Intellectual foundations / sources of authority */}
    <div className="reveal flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-4 mb-10" data-testid="doctrine-foundations">
      <BookOpen size={16} className="text-teal-300 mt-0.5 shrink-0" />
      <p className="text-[13px] text-zinc-300 leading-relaxed">
        <span className="text-teal-300/90 uppercase tracking-[0.18em] text-[11px] mr-2">Informed by</span>
        person-centered listening, Motivational Interviewing, trauma-informed care, Psychological First Aid,
        Stress First Aid, cognitive and acceptance-based reflection, and ANA nurse well-being frameworks.
      </p>
    </div>

    {/* Six doctrines */}
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
      {doctrines.map((d, i) => (
        <div key={d.title} className="reveal glass rounded-2xl p-6" style={{ transitionDelay: `${i * 50}ms` }} data-testid={`doctrine-${i}`}>
          <div className="flex items-center gap-3 mb-3">
            <span className="w-10 h-10 rounded-xl bg-white/[0.05] border border-teal-300/25 flex items-center justify-center shrink-0">
              <d.icon size={18} className="text-teal-300" />
            </span>
            <span className="font-heading text-xs text-teal-300/80">{String(i + 1).padStart(2, "0")}</span>
          </div>
          <h4 className="font-heading text-white text-[15px] leading-tight mb-1.5">{d.title}</h4>
          <p className="text-[13.5px] text-zinc-300 leading-relaxed">{d.body}</p>
        </div>
      ))}
    </div>

    {/* What a nurse hears */}
    <div className="reveal grid md:grid-cols-2 gap-4 mb-8">
      <div className="glass rounded-2xl p-7">
        <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-300 mb-4">What a nurse might hear</div>
        <ul className="space-y-3">
          {wouldSay.map((t) => (
            <li key={t} className="flex items-start gap-3 text-[14px] text-zinc-100 leading-relaxed">
              <Check size={16} className="text-emerald-300 mt-0.5 shrink-0" /> <span>&ldquo;{t}&rdquo;</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="glass rounded-2xl p-7">
        <div className="text-[11px] uppercase tracking-[0.22em] text-rose-300/80 mb-4">What it will never say</div>
        <ul className="space-y-3">
          {wouldNever.map((t) => (
            <li key={t} className="flex items-start gap-3 text-[14px] text-zinc-300 leading-relaxed">
              <X size={16} className="text-rose-300/80 mt-0.5 shrink-0" /> <span className="line-through decoration-rose-300/50">&ldquo;{t}&rdquo;</span>
            </li>
          ))}
        </ul>
      </div>
    </div>

    {/* Four-line creed */}
    <div className="reveal glass rounded-2xl p-7 mb-8" data-testid="doctrine-creed">
      <div className="text-[11px] uppercase tracking-[0.22em] text-teal-300 mb-4">The behavioral boundary</div>
      <p className="font-heading text-white text-[15px] sm:text-base leading-relaxed">
        Recognition is not diagnosis. Support is not treatment.<br className="hidden sm:block" />
        Guidance is not command. Awareness is not surveillance.
      </p>
    </div>

    {/* Governance + download */}
    <div className="reveal glass-strong rounded-2xl p-7 sm:p-8 mb-6" data-testid="doctrine-governance">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
        <p className="text-[13.5px] text-zinc-200 leading-relaxed max-w-2xl">
          Each principle would be translated into testable conversational rules, nurse scenarios, review standards,
          and audit requirements.
        </p>
        <a
          href="/MMG-ANA-Behavioral-Support-Doctrine.pdf" target="_blank" rel="noopener noreferrer"
          data-testid="doctrine-download"
          className="shrink-0 inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-white/10 border border-teal-300/25 text-white font-heading font-medium text-sm tracking-tight hover:bg-white/15 hover:border-teal-300/40 transition"
        >
          <FileText size={15} /> Explore the Full Behavioral Doctrine
        </a>
      </div>
    </div>

    {/* Transition into governance */}
    <p className="reveal text-center text-[13px] text-zinc-400 italic max-w-2xl mx-auto leading-relaxed">
      A doctrine creates the behavioral boundary. Governance ensures that boundary remains visible, testable, and accountable.
    </p>
  </div>
);

export default Doctrine;
